import React from "react";
import styled from "@emotion/styled";
import { Button, Select, Typography, message } from "antd";
import { FileSearchOutlined } from "@ant-design/icons";
import {
  AgentContainerOption,
  fetchAgentContainerOptions,
} from "../../services/agentPortalService";

const { Text } = Typography;

interface AgentConsolidationBarProps {
  selectedCount: number;
  missingCiCount: number;
  onAnalyseCi: () => Promise<void>;
  analysingCi: boolean;
  /** Return true when bookings were linked so the container select can reset. */
  onConsolidate: (containerId: number) => Promise<boolean>;
  consolidating: boolean;
  onClearSelection?: () => void;
}

const FloatingBar = styled.div`
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  width: min(960px, calc(100vw - 24px));
  padding: 12px 14px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.08),
    0 3px 6px rgba(0, 0, 0, 0.06);
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    bottom: 12px;
    width: calc(100vw - 16px);
    padding: 12px;
    flex-direction: column;
    align-items: stretch;
  }
`;

const BarRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BarActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-left: auto;

  @media (max-width: 767px) {
    margin-left: 0;
    width: 100%;

    .ant-btn {
      flex: 1;
    }
  }
`;

const formatContainerLabel = (container: AgentContainerOption) => {
  const transport = Array.isArray(container.transport_type_info)
    ? container.transport_type_info[0]?.name
    : container.transport_type_info?.name;
  const ref = container.reference || `Container ${container.id}`;
  const status = container.status ? ` · ${container.status}` : "";
  const type = transport ? ` · ${transport}` : "";
  return `${ref}${type}${status}`;
};

const Divider = styled.div`
  width: 1px;
  align-self: stretch;
  background: #f0f0f0;
  margin: 0 4px;

  @media (max-width: 767px) {
    display: none;
  }
`;

export const AgentConsolidationBar: React.FC<AgentConsolidationBarProps> = ({
  selectedCount,
  missingCiCount,
  onAnalyseCi,
  analysingCi,
  onConsolidate,
  consolidating,
  onClearSelection,
}) => {
  const [containerId, setContainerId] = React.useState<number>();
  const [containers, setContainers] = React.useState<AgentContainerOption[]>(
    []
  );
  const [loadingContainers, setLoadingContainers] = React.useState(false);

  React.useEffect(() => {
    if (selectedCount === 0) {
      setContainerId(undefined);
      return;
    }

    const load = async () => {
      setLoadingContainers(true);
      const { data, error } = await fetchAgentContainerOptions();
      if (error) {
        message.error("Failed to load containers");
      }
      setContainers(data);
      setLoadingContainers(false);
    };
    load();
  }, [selectedCount]);

  const handleSubmit = async () => {
    if (!containerId) {
      message.warning("Select a container");
      return;
    }
    const linked = await onConsolidate(containerId);
    if (linked) {
      setContainerId(undefined);
    }
  };

  if (selectedCount === 0) {
    return null;
  }

  return (
    <FloatingBar>
      <BarRow>
        <Text strong style={{ whiteSpace: "nowrap" }}>
          {selectedCount} selected
        </Text>
        <Button
          icon={<FileSearchOutlined />}
          loading={analysingCi}
          disabled={missingCiCount === 0 || consolidating}
          onClick={() => void onAnalyseCi()}
        >
          Analyse CI
          {missingCiCount > 0 ? ` (${missingCiCount})` : ""}
        </Button>
      </BarRow>
      <Divider />
      <BarRow style={{ flex: 1, minWidth: 0 }}>
        <Text type="secondary">Add to consolidation</Text>
      </BarRow>
      <Select
        showSearch
        allowClear
        placeholder="Select container"
        value={containerId}
        onChange={setContainerId}
        loading={loadingContainers}
        optionFilterProp="label"
        style={{ flex: 1, minWidth: 0, width: "100%" }}
        options={containers.map((c) => ({
          value: c.id,
          label: formatContainerLabel(c),
        }))}
      />
      <BarActions>
        <Button
          type="primary"
          loading={consolidating}
          disabled={!containerId}
          onClick={handleSubmit}
        >
          Add {selectedCount} booking{selectedCount === 1 ? "" : "s"}
        </Button>
        {onClearSelection ? (
          <Button onClick={onClearSelection}>Clear</Button>
        ) : null}
      </BarActions>
    </FloatingBar>
  );
};
